import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowRight, FileCsv, Warning, SealCheck, Images } from "@phosphor-icons/react";

// A few REAL rows from a Chairish CSV export — the "skeleton" you're handed.
const SKELETON_ROWS = [
  { title: "Signed Pair of Vintage Murano Mazzega Style Sp…", price: "865", pid: "29050938" },
  { title: "1920s French Louis XVI Style Carved Window Arm…", price: "675", pid: "5142363" },
  { title: "A Restored Pair French Country Chic Two-Tier S…", price: "1750", pid: "9132010" },
  { title: "Large Vintage Mirror From the Hills of Tuscany…", price: "375", pid: "9139482" },
  { title: "Early 20th Century Antique Round Wicker Victor…", price: "225", pid: "9204476" },
];

// The SAME five pieces, recovered — real values and real photos from the seller's own inventory.
const RICH_ROWS = [
  {
    pid: "29050938", price: "865", photos: 9,
    title: "Signed Pair of Vintage Murano Mazzega Style Splatter Glass Table Lamps With Detailed Cutout and Dolphin Base",
    maker: "Murano", era: "Mid 20th Century", cat: "Lighting",
    dims: "15 × 15 × 23 in", materials: "Murano Glass, Matte Brass",
    desc: "This signed piece has all the hallmarks of an excellent example of the Murano Art Deco era…",
  },
  {
    pid: "5142363", price: "675", photos: 10,
    title: "1920s French Louis XVI Style Carved Window Arm Bench With Original Brocade Upholstery",
    maker: "Louis XVI Style", era: "1920s", cat: "Seating",
    dims: "75 × 19 × 29 in", materials: "Carved Wood, Brocade",
    desc: "Carved window arm bench with its original brocade upholstery…",
  },
  {
    pid: "9132010", price: "1750", photos: 7,
    title: "A Restored Pair French Country Chic Two-Tier Side Tables",
    maker: "French Country", era: "Early 20th Century", cat: "Tables",
    dims: "18 × 36 × 24 in", materials: "Wood, Cast Iron, Paint",
    desc: "Gorgeously distressed, restored antique-white tables — flawless, with authentic cast-iron hardware added to the front of each…",
  },
  {
    pid: "9139482", price: "375", photos: 7,
    title: "Large Vintage Mirror From the Hills of Tuscany Collection by Thomasville",
    maker: "Thomasville", era: "Late 20th Century", cat: "Mirrors & Art",
    dims: "57 × 2 × 44 in", materials: "Mirror, Wood",
    desc: "Stunning large-scale vintage mirror from Thomasville's beloved Hills of Tuscany Collection — a gorgeous statement piece…",
  },
  {
    pid: "9204476", price: "225", photos: 6,
    title: "Early 20th Century Antique Round Wicker Victorian Accent Table",
    maker: "Victorian", era: "Early 20th Century", cat: "Tables",
    dims: "26 × 26 × 25 in", materials: "Wicker, Paint",
    desc: "Gorgeous early American classic round wicker side table — substantial in size, with two tiers of storage…",
  },
];

function Frame({ src, alt }) {
  return (
    <div className="rounded-xl overflow-hidden border border-[#0D1B2A]/15 shadow-xl bg-white">
      <div className="h-7 bg-[#EDE9E1] flex items-center gap-1.5 px-3 border-b border-[#0D1B2A]/10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#D96C57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#E3B341]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#7FB77E]" />
      </div>
      <div className="aspect-[16/9] overflow-hidden bg-[#FDFBF7]">
        <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover object-top" />
      </div>
    </div>
  );
}

function Stage({ n, kicker, title, sub, last, children }) {
  return (
    <div className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-8" data-testid={`transform-stage-${n}`}>
      <div className="flex md:flex-col items-center md:items-center gap-3">
        <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
          <div className="absolute inset-0 rotate-45 bg-[#B47A3C]" />
          <span className="relative font-serif-display text-xl font-bold text-[#FDFBF7]">{n}</span>
        </div>
        {!last && <div className="hidden md:block flex-1 w-px bg-[#0D1B2A]/15" />}
      </div>
      <div className="pb-4">
        <span className="label-caps text-[#B47A3C]" style={{ letterSpacing: "0.18em" }}>{kicker}</span>
        <h3 className="font-serif-display text-2xl sm:text-3xl font-medium tracking-tight text-[#0D1B2A] mt-1">{title}</h3>
        <p className="text-[#525252] leading-relaxed mt-2 max-w-2xl">{sub}</p>
        <div className="mt-5 max-w-3xl">{children}</div>
      </div>
    </div>
  );
}

function SkeletonCsv() {
  return (
    <div className="rounded-xl border border-[#0D1B2A]/15 shadow-md overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-[#EDE9E1] border-b border-[#0D1B2A]/10">
        <FileCsv size={18} weight="fill" className="text-[#6B7280]" />
        <span className="text-sm font-semibold text-[#3A3A3A]">chairish_active_listings.csv</span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-[#B4451F] font-semibold">
          <Warning size={13} weight="fill" /> no photos · no details
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px] text-[#57534E]">
          <thead>
            <tr className="border-b border-[#0D1B2A]/10 text-[11px] uppercase tracking-wide text-[#8A8577]">
              <th className="px-4 py-2 font-semibold">Title</th>
              <th className="px-4 py-2 font-semibold">Price</th>
              <th className="px-4 py-2 font-semibold">Product ID</th>
              <th className="px-4 py-2 font-semibold">State</th>
            </tr>
          </thead>
          <tbody>
            {SKELETON_ROWS.map((r) => (
              <tr key={r.pid} className="border-b border-[#0D1B2A]/5">
                <td className="px-4 py-2 whitespace-nowrap">{r.title}</td>
                <td className="px-4 py-2 tabular-nums">${r.price}</td>
                <td className="px-4 py-2 tabular-nums text-[#9CA3AF]">{r.pid}</td>
                <td className="px-4 py-2 text-[#9CA3AF]">Listed</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RichInventoryTable() {
  return (
    <div className="rounded-xl border border-[#0D1B2A]/15 shadow-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-[#0D1B2A] border-b border-[#0D1B2A]/10">
        <FileCsv size={18} weight="fill" className="text-[#B47A3C]" />
        <span className="text-sm font-semibold text-[#FDFBF7]">your_recovered_inventory · Vintage Vantage</span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-[#7FB77E] font-semibold">
          <SealCheck size={13} weight="fill" /> descriptions · photos · measurements — everything recovered
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px] text-[#57534E]" style={{ minWidth: 920 }}>
          <thead>
            <tr className="border-b border-[#0D1B2A]/10 text-[11px] uppercase tracking-wide text-[#8A8577] bg-[#F5F3ED]">
              <th className="px-4 py-2 font-semibold">Photo</th>
              <th className="px-4 py-2 font-semibold">Piece & description</th>
              <th className="px-4 py-2 font-semibold">Maker · Era</th>
              <th className="px-4 py-2 font-semibold">Measurements & materials</th>
              <th className="px-4 py-2 font-semibold">Category</th>
              <th className="px-4 py-2 font-semibold">Photos</th>
              <th className="px-4 py-2 font-semibold text-right">Price</th>
            </tr>
          </thead>
          <tbody>
            {RICH_ROWS.map((r) => (
              <tr key={r.pid} className="border-b border-[#0D1B2A]/5 align-top" data-testid={`rich-row-${r.pid}`}>
                <td className="px-4 py-3">
                  <img src={`/howitworks/thumbs/${r.pid}.jpg`} alt={r.title} loading="lazy" className="w-16 h-16 rounded-md object-cover border border-[#0D1B2A]/10 shadow-sm" />
                </td>
                <td className="px-4 py-3 max-w-[300px]">
                  <p className="font-semibold text-[#0D1B2A] leading-snug line-clamp-2">{r.title}</p>
                  <p className="text-xs text-[#8A8577] leading-snug mt-1 line-clamp-2 italic">{r.desc}</p>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <p className="font-semibold text-[#0D1B2A]">{r.maker}</p>
                  <p className="text-xs text-[#8A8577] mt-0.5">{r.era}</p>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <p className="tabular-nums text-[#0D1B2A]">{r.dims}</p>
                  <p className="text-xs text-[#8A8577] mt-0.5">{r.materials}</p>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{r.cat}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0D1B2A] border border-[#0D1B2A]/15 bg-[#FBFAF6] rounded-full px-2 py-0.5">
                    <Images size={12} weight="fill" className="text-[#B47A3C]" /> {r.photos} photos
                  </span>
                </td>
                <td className="px-4 py-3 tabular-nums font-semibold text-[#0D1B2A] text-right">${r.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TransformationStory() {
  const navigate = useNavigate();
  return (
    <section className="bg-[#FDFBF7] border-b border-[#0D1B2A]/10" data-testid="transformation-story">
      <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <div className="max-w-3xl mb-12">
          <span className="label-caps text-[#B47A3C]" style={{ letterSpacing: "0.2em" }}>See it in action · real inventory</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-[#0D1B2A] mt-4">
            Watch a bare export become a business you own.
          </h2>
          <p className="text-base text-[#525252] leading-relaxed mt-5">
            This isn't a mock-up. Every screen below is real inventory — recovered, rebuilt, and made ready
            to list on the marketplaces you choose.
          </p>
        </div>

        {/* BEFORE / AFTER — the raw export vs. the same five pieces, fully recovered */}
        <div className="mb-16" data-testid="inventory-before-after">
          <div className="max-w-3xl mb-8">
            <h3 className="font-serif-display text-2xl sm:text-3xl font-medium tracking-tight text-[#0D1B2A]">
              What a raw export gives you — <span className="italic text-[#B47A3C]">vs. what we recover.</span>
            </h3>
            <p className="text-[#525252] leading-relaxed mt-2">
              If you've ever tried to pull your inventory out of a marketplace, you know this spreadsheet.
              It's the thin export that holds sellers hostage to a single platform.
            </p>
          </div>

          {/* BEFORE */}
          <div data-testid="before-export-card" className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-3 bg-[#EDE9E1] border border-[#0D1B2A]/15 px-3 py-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8577]">Before</span>
              <span className="text-[13px] font-semibold text-[#3A3A3A]">Marketplace export</span>
            </div>
            <SkeletonCsv />
            <p className="text-sm text-[#8A8577] mt-3 leading-snug">
              Thin columns — titles, prices, IDs. No descriptions, no measurements, no photos. Years of
              work, flattened into a spreadsheet you can't actually reuse.
            </p>
          </div>

          {/* Arrow + the recovery */}
          <div className="flex items-center gap-4 my-8" data-testid="recovery-bridge">
            <ArrowDown size={28} weight="bold" className="text-[#B47A3C] shrink-0" />
            <p className="text-[15px] text-[#33302B] leading-snug max-w-2xl">
              <strong className="text-[#0D1B2A]">Vintage Vantage reads your product IDs</strong>, recovers your
              published details, and transforms them into a complete seller-owned inventory.
            </p>
          </div>

          {/* AFTER — same five pieces, real values, real photos */}
          <div data-testid="after-recovered-card">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div className="inline-flex items-center gap-2 bg-[#0D1B2A] px-3 py-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#B47A3C]">After</span>
                <span className="text-[13px] font-semibold text-[#FDFBF7]">The same five pieces — recovered</span>
              </div>
              <span className="inline-flex items-center text-[11px] font-bold uppercase tracking-[0.12em] text-[#B47A3C] border border-[#B47A3C]/40 px-2.5 py-1">
                Centralized · Portable · Seller-Owned
              </span>
            </div>
            <RichInventoryTable />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D1B2A]">
                <Images size={16} weight="fill" className="text-[#B47A3C]" /> Real listings, real photos — 195 pieces · 378 photos · $138K recovered in this catalog
              </span>
            </div>
            <p className="text-sm text-[#57534E] mt-2 leading-snug max-w-2xl">
              Complete records — descriptions, photos, measurements, maker, category. Everything recovered,
              exactly as it appears in your own inventory.
            </p>
            <button
              onClick={() => navigate("/register")}
              data-testid="recover-inventory-cta"
              className="mt-6 h-14 px-8 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-95"
            >
              Recover my inventory <ArrowRight size={18} weight="bold" />
            </button>
            <p className="text-xs text-[#8A8577] mt-2">Free to start — upload your marketplace export and watch it come back to life.</p>
          </div>

          <p className="font-serif-display text-xl sm:text-2xl italic text-[#0D1B2A] border-l-2 border-[#B47A3C] pl-4 mt-10 max-w-2xl" data-testid="ownership-philosophy">
            Countless hours of work should belong to you — not remain trapped inside a marketplace.
          </p>
        </div>

        <div className="space-y-4">
          {/* 1 — New find → draft */}
          <Stage
            n={1}
            kicker="A brand-new find"
            title="Drafted before you've even unpacked it."
            sub="Snap a new piece — this Butler Specialty wine rack — and it lands as a structured draft: photos organized, headline written, maker matched from your own past listings. We pre-fill what we can verify. You provide your expertise. Final approval is always yours."
          >
            <Frame src="/howitworks/winerack_draft.jpg" alt="The Butler wine rack, auto-drafted with photos and details" />
          </Stage>

          <div className="flex justify-center"><ArrowDown size={26} weight="bold" className="text-[#B47A3C]" /></div>

          {/* 2 — Ready to cross-list */}
          <Stage
            n={2}
            kicker="Checked, then cross-listed"
            title="Ready to list on the marketplaces you choose."
            sub="A readiness check confirms each marketplace's must-haves before you publish — 12 of 12 for Chairish, plus 1stDibs, AptDeco & Pamono — so your listing is never turned away after all your work."
          >
            <Frame src="/howitworks/winerack_listing.jpg" alt="Cross-list readiness — 100% ready across your chosen marketplaces" />
          </Stage>

          <div className="flex justify-center"><ArrowDown size={26} weight="bold" className="text-[#B47A3C]" /></div>

          {/* 3 — Sold */}
          <Stage
            n={3}
            last
            kicker="The whole point"
            title="…and it sells."
            sub="One record. Cross-list once. Keep the return."
          >
            <div className="rounded-xl border border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] vv-grain overflow-hidden shadow-xl">
              <div className="flex items-center gap-2 px-6 py-3 border-b border-white/10">
                <SealCheck size={18} weight="fill" className="text-[#7FB77E]" />
                <span className="label-caps text-[#7FB77E]" style={{ letterSpacing: "0.18em" }}>Sold on Chairish</span>
                <span className="ml-auto text-xs text-[#8A867D] tabular-nums">Order #1360399</span>
              </div>
              <div className="px-6 sm:px-9 py-9">
                <p className="text-[#8A867D] text-xs uppercase tracking-wide">A real sale, from a real seller</p>
                <p className="font-serif-display text-2xl sm:text-3xl mt-3 leading-snug">
                  &ldquo;Congrats on your sold Classic Early 20th-Century Queen Anne Style Writing Desk in Walnut Burlwood Veneer.&rdquo;
                </p>
                <p className="text-[#8A867D] text-sm mt-4">— Team Chairish</p>
                <div className="h-px bg-white/10 my-7" />
                <p className="text-[#FDFBF7]/90 max-w-xl leading-relaxed">
                  <strong className="text-[#B47A3C]">Do the work once, and let it keep paying you.</strong>
                </p>
              </div>
            </div>
          </Stage>
        </div>
      </div>
    </section>
  );
}
